// Session PJ2: put a different engineer on a booked survey visit. It uses the same route the Schedule page's own reassign uses
// (PATCH /api/schedule/jobs/:id), so the engineer is told, the calendar is updated and the capacity and leave warnings appear here as they
// do there. The day is moved on the Schedule page, not here.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, type ScheduleEngineer } from "../../../lib/api";
import { friendlyApiError } from "../../../lib/friendlyApiError";
import { MODAL_ACTIONS, MODAL_PRIMARY_BTN, MODAL_SECONDARY_BTN, ModalShell } from "../../../components/ModalShell";

const inputCls = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green disabled:opacity-50 max-sm:min-h-11";

export function ReassignVisitModal({
  jobId,
  currentEngineerName,
  onClose,
  onDone,
}: {
  jobId: string;
  currentEngineerName: string | null;
  onClose: () => void;
  /** The reassign went through; `warnings` are the schedule route's own, in words. */
  onDone: (engineerName: string, warnings: string[]) => void;
}) {
  const [engineers, setEngineers] = useState<ScheduleEngineer[]>([]);
  const [engineerId, setEngineerId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    api.scheduling
      .engineers()
      .then((e) => {
        if (alive) setEngineers(e);
      })
      .catch(() => {
        if (alive) setError("Could not load the engineer list. Please try again.");
      });
    return () => {
      alive = false;
    };
  }, []);

  async function go() {
    if (!engineerId) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.schedule.reassign(jobId, { assignedTo: engineerId });
      const warnings = [res.capacityWarning, res.leaveWarning, res.certificationWarning].filter((w): w is string => !!w);
      onDone(engineers.find((e) => e.id === engineerId)?.name ?? "the engineer", warnings);
    } catch (e) {
      setError(friendlyApiError(e, "Could not change the engineer."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell ariaLabel="Change the engineer" onClose={() => !busy && onClose()} width="max-w-md" panelClassName="p-5">
      <h2 className="font-heading text-xl text-ele-strong">Change the engineer</h2>
      <p className="mt-1 text-sm text-ele-body">
        {currentEngineerName ? `${currentEngineerName} is on this visit now. ` : "Nobody is on this visit yet. "}
        The new engineer is told. To move the day, use the <Link to="/office/scheduling" className="text-ele-green underline-offset-2 hover:underline">Schedule</Link>.
      </p>
      <label htmlFor="reassign-engineer" className="mb-1 mt-4 block font-menu text-xs uppercase tracking-wide text-ele-body">Engineer</label>
      <select id="reassign-engineer" className={inputCls} value={engineerId} onChange={(e) => setEngineerId(e.target.value)}>
        <option value="">Choose...</option>
        {engineers.map((e) => (
          <option key={e.id} value={e.id}>{e.name}</option>
        ))}
      </select>
      {error ? <p role="alert" className="mt-3 rounded-ele border border-ele-danger px-3 py-2 text-sm text-ele-strong">{error}</p> : null}
      <div className={`mt-5 gap-3 ${MODAL_ACTIONS}`}>
        <button type="button" className={MODAL_SECONDARY_BTN} onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" className={MODAL_PRIMARY_BTN} onClick={() => void go()} disabled={busy || !engineerId}>
          {busy ? "Changing..." : "Change the engineer"}
        </button>
      </div>
    </ModalShell>
  );
}
